import React from 'react';
import styled, { ThemeProps } from 'styled-components';
import { ProfileImage } from 'components/ProfileImage';
import { Card } from 'styleguide/Card';
import { View } from 'styleguide/View';
import { H2, H3, P2, P3 } from 'styleguide/Texts';
import { FollowButtonPrimary } from 'components/FollowButton';
import { ProfileStat } from 'components/ProfileStat';
import { CornerRadius, Spacing } from 'styleguide/spacing';
import { SecondaryButton } from 'styleguide/Buttons';
import router from 'next/router';
import { colors } from 'styleguide/colors';
import { formatLink } from 'utils/misc';
import { Theme } from 'styleguide/types';
import { FlexRow } from 'styleguide/Row';
import { stores } from 'state/stores';
import { t } from 'translations/t';

const PROFILE_IMAGE_DIAMETER = 104;

interface ProfileHeaderProps {
  username: string;
  fullname?: string;
  profilePic?: string;
  imageUrls?: string[];
  link?: string;
  workoutCount: string;
  followerCount: string;
  onFollowersPress?: () => void;
  followingCount: string;
  onFollowingPress?: () => void;
  description?: string;
  showFollowButton: boolean;
  isOwner?: boolean;
  isPrivate?: boolean;
}

export const ProfileHeader = (props: ProfileHeaderProps) => {
  return (
    <>
      <DesktopHeader {...props} />
      <MobileHeader {...props} />
    </>
  );
};

const DesktopContainer = styled(Card)`
  display: flex;
  flex-direction: column;
  @media (max-width: 1024px) {
    display: none;
  }
`;

const StyledImage = styled.img<{ position: 'first' | 'middle' | 'last' }>`
  display: flex;
  width: 100%;
  min-width: 100px;
  border-top-left-radius: ${props => (props.position === 'first' ? `${CornerRadius.sm}px` : 0)};
  border-bottom-left-radius: 0px;
  border-bottom-right-radius: 0px;
  border-top-right-radius: ${props => (props.position === 'last' ? `${CornerRadius.sm}px` : 0)};
`;

const ImagePlaceholder = styled.div<{ position: 'middle' | 'last' }>`
  display: flex;
  width: 100%;
  align-self: stretch;
  border-bottom-right-radius: ${props => (props.position === 'last' ? `${CornerRadius.sm}px` : 0)};
  border-top-right-radius: ${props => (props.position === 'last' ? `${CornerRadius.sm}px` : 0)};
  background-color: ${(props: ThemeProps<Theme>) =>
    props.theme.name === 'light' ? colors.greyUltraLight : colors.greyUltraDark};
`;

const ProfileImageContainer = styled.div<{ radius: number }>`
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${(props: ThemeProps<Theme>) => props.theme.secondary};
  border-radius: ${props => props.radius + 2}px;
  height: ${props => props.radius * 2 + 4}px;
  width: ${props => props.radius * 2 + 4}px;
`;

const StatsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.md}px;
`;

const DesktopHeader = ({
  profilePic,
  fullname,
  username,
  link,
  imageUrls,
  isOwner,
  isPrivate,
  workoutCount,
  followerCount,
  onFollowersPress,
  followingCount,
  onFollowingPress,
  description,
  showFollowButton,
}: ProfileHeaderProps) => {
  const hasWorkoutImages = !!imageUrls && imageUrls.length !== 0;

  const isFollowing = stores.followingStatus.status(username) === 'following';
  const clickableStatsEnabled = isOwner || !isPrivate || isFollowing;

  return (
    <DesktopContainer>
      {hasWorkoutImages && (
        <FlexRow style={{ gap: 2, marginBottom: -PROFILE_IMAGE_DIAMETER }}>
          {imageUrls
            .concat(new Array(5).fill('empty'))
            .slice(0, 5)
            .map((url, index, array) =>
              url !== 'empty' ? (
                <StyledImage
                  src={url}
                  key={index}
                  position={index === 0 ? 'first' : index === array.length - 1 ? 'last' : 'middle'}
                />
              ) : (
                <ImagePlaceholder
                  key={index}
                  position={index === array.length - 1 ? 'last' : 'middle'}
                />
              ),
            )}
        </FlexRow>
      )}
      <View style={{ padding: Spacing.lg, gap: Spacing.sm }}>
        <ProfileImageContainer radius={PROFILE_IMAGE_DIAMETER / 2 + 2}>
          <ProfileImage source={profilePic} diameter={PROFILE_IMAGE_DIAMETER} />
        </ProfileImageContainer>

        <View style={{ gap: Spacing.md }}>
          <FlexRow style={{ alignItems: 'center', gap: Spacing.md }}>
            <View style={{ minWidth: 172 }}>
              <H2>{username}</H2>
              <P2 type="secondary">{fullname}</P2>
            </View>

            {isOwner && (
              <SecondaryButton
                title={t('web.profile.profileHeader.editProfile')}
                onClick={() => router.push('/settings?profile')}
              />
            )}

            {!isOwner && showFollowButton && (
              <FollowButtonPrimary buttonStyle={{ width: 75 }} username={username} />
            )}
          </FlexRow>

          <StatsContainer>
            <ProfileStat
              label={t('web.profileStat.workouts')}
              count={`${workoutCount}`}
              disabled={!clickableStatsEnabled}
            />
            <ProfileStat
              label={t('web.profileStat.followers')}
              count={`${followerCount}`}
              onClick={onFollowersPress}
              disabled={!clickableStatsEnabled}
            />
            <ProfileStat
              label={t('web.profileStat.following')}
              count={`${followingCount}`}
              onClick={onFollowingPress}
              disabled={!clickableStatsEnabled}
            />
          </StatsContainer>

          {(description || link) && (
            <View>
              {description && <P2 style={{ whiteSpace: 'pre-wrap' }}>{description}</P2>}
              {link && (
                <a
                  href={formatLink(link)}
                  style={{ textDecoration: 'none' }}
                  target="_blank"
                  rel="noreferrer"
                >
                  <P2 style={{ color: colors.primary, whiteSpace: 'pre-wrap' }}>{link}</P2>
                </a>
              )}
            </View>
          )}
        </View>
      </View>
    </DesktopContainer>
  );
};

const MobileContainer = styled(Card)`
  display: flex;
  flex-direction: column;
  @media (min-width: 1024px) {
    display: none;
  }
`;

const MobileHeader = ({
  profilePic,
  username,
  fullname,
  link,
  imageUrls,
  workoutCount,
  followerCount,
  onFollowersPress,
  followingCount,
  onFollowingPress,
  description,
  showFollowButton,
  isOwner,
  isPrivate,
}: ProfileHeaderProps) => {
  const isFollowing = stores.followingStatus.status(username) === 'following';
  const clickableStatsEnabled = isOwner || !isPrivate || isFollowing;

  return (
    <MobileContainer>
      {!!imageUrls && imageUrls.length !== 0 && (
        <FlexRow style={{ gap: 2, padding: Spacing.sm, marginBottom: -45 }}>
          {imageUrls
            .concat(new Array(3).fill('empty'))
            .slice(0, 3)
            .map((url, index, array) =>
              url !== 'empty' ? (
                <StyledImage
                  src={url}
                  key={index}
                  position={index === 0 ? 'first' : index === array.length - 1 ? 'last' : 'middle'}
                ></StyledImage>
              ) : (
                <ImagePlaceholder
                  key={index}
                  position={index === array.length - 1 ? 'last' : 'middle'}
                ></ImagePlaceholder>
              ),
            )}
        </FlexRow>
      )}
      <View style={{ padding: Spacing.lg, gap: Spacing.md }}>
        <View style={{ flexDirection: 'row', alignItems: 'flex-end', gap: Spacing.lg }}>
          <ProfileImageContainer radius={36}>
            <ProfileImage source={profilePic} diameter={72} />
          </ProfileImageContainer>
          <View style={{ gap: Spacing.xs }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: Spacing.md }}>
              <H3>{username}</H3>
            </View>
            <P2 type="secondary">{fullname}</P2>
          </View>
        </View>
        <StatsContainer>
          <ProfileStat
            label={t('web.profileStat.workouts')}
            count={`${workoutCount}`}
            disabled={!clickableStatsEnabled}
          />
          <ProfileStat
            label={t('web.profileStat.followers')}
            count={`${followerCount}`}
            onClick={onFollowersPress}
            disabled={!clickableStatsEnabled}
          />
          <ProfileStat
            label={t('web.profileStat.following')}
            count={`${followingCount}`}
            onClick={onFollowingPress}
            disabled={!clickableStatsEnabled}
          />
        </StatsContainer>

        {isOwner && (
          <SecondaryButton
            title={t('web.profile.profileHeader.editProfile')}
            onClick={() => {
              router.push('/settings?profile');
            }}
          ></SecondaryButton>
        )}
        {!isOwner && showFollowButton && (
          <FollowButtonPrimary buttonStyle={{ width: '100%' }} username={username} />
        )}

        {description && <P2 style={{ whiteSpace: 'pre-wrap' }}>{description}</P2>}

        {link && (
          <a
            href={formatLink(link)}
            style={{ textDecoration: 'none' }}
            target="_blank"
            rel="noreferrer"
          >
            <P3 style={{ color: colors.primary, whiteSpace: 'pre-wrap' }}>{link}</P3>
          </a>
        )}
      </View>
    </MobileContainer>
  );
};
